import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  ChevronLeft, Plus, Volume2, VolumeX, RotateCcw,
  Trophy, Loader2, Home, Target, ArrowRight
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { TriangleCheckersGame } from './triangleCheckersLogic';
import { executeAIMove } from './triangleCheckersAI';

// Hexagon cell component
const HexCell = ({ cell, piece, isSelected, isValidMove, onClick, hexSize }) => {
  const { q, r, zone } = cell;
  
  // Convert axial to pixel coordinates
  const x = hexSize * (3/2 * q);
  const y = hexSize * (Math.sqrt(3) * (r + q/2));
  
  // Determine zone colors
  const getZoneColor = () => {
    switch (zone) {
      case 'point0': return 'fill-blue-600/40 stroke-blue-400/60'; // Player 1 home
      case 'point3': return 'fill-amber-600/40 stroke-amber-400/60'; // Player 2 home (P1 goal)
      case 'point1':
      case 'point4': return 'fill-emerald-700/30 stroke-emerald-500/40';
      case 'point2':
      case 'point5': return 'fill-purple-700/30 stroke-purple-500/40';
      default: return 'fill-slate-700/50 stroke-slate-500/30';
    }
  };
  
  // Hexagon path (pointy-top)
  const hexPath = useMemo(() => {
    const points = [];
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i - Math.PI / 6;
      points.push(`${Math.cos(angle) * (hexSize * 0.9)},${Math.sin(angle) * (hexSize * 0.9)}`);
    }
    return `M ${points.join(' L ')} Z`;
  }, [hexSize]);
  
  return (
    <g 
      transform={`translate(${x}, ${y})`}
      onClick={onClick}
      className="cursor-pointer"
    >
      {/* Hex background */}
      <motion.path
        d={hexPath}
        className={`
          ${getZoneColor()}
          ${isSelected ? 'fill-yellow-500/50 stroke-yellow-400 stroke-2' : ''}
          ${isValidMove && !piece ? 'fill-green-500/40 stroke-green-400 stroke-2' : ''}
          transition-colors
        `}
        initial={false}
        animate={{ 
          scale: isSelected ? 1.05 : 1,
        }}
        whileHover={{ scale: 1.08 }}
      />
      
      {/* Valid move indicator */}
      {isValidMove && !piece && (
        <motion.circle
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          r={hexSize * 0.25}
          className="fill-green-400/70"
        />
      )}
      
      {/* Piece */}
      {piece && (
        <motion.circle
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          r={hexSize * 0.55}
          className={`
            ${piece.player === 1 
              ? 'fill-blue-500 stroke-blue-300 stroke-2' 
              : 'fill-amber-500 stroke-amber-300 stroke-2'
            }
            ${isSelected ? 'stroke-white stroke-[3px]' : ''}
            drop-shadow-lg
          `}
          style={{
            filter: `drop-shadow(0 2px 4px ${piece.player === 1 ? 'rgba(59,130,246,0.5)' : 'rgba(245,158,11,0.5)'})`
          }}
        />
      )}
      
      {/* Inner piece detail */}
      {piece && (
        <circle
          r={hexSize * 0.35}
          className={`
            ${piece.player === 1 ? 'fill-blue-400' : 'fill-amber-400'}
            opacity-60
          `}
        />
      )}
    </g>
  );
};

// New Game Dialog
const NewGameDialog = ({ isOpen, onClose, onStart }) => {
  const [playerNumber, setPlayerNumber] = useState(1);
  const [difficulty, setDifficulty] = useState('medium');
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-cyan-500/30 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-cyan-400 flex items-center gap-2">
            <Target className="w-5 h-5" />
            New Triangle Checkers Game
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          <p className="text-gray-400 text-sm">
            Race your pieces from your starting corner to the opposite corner. 
            Move to adjacent cells or hop over pieces to travel faster!
          </p>
          
          {/* Player Selection */}
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Play as</label>
            <div className="flex gap-3">
              <button
                onClick={() => setPlayerNumber(1)}
                className={`
                  flex-1 p-4 rounded-xl border-2 transition-all
                  ${playerNumber === 1 
                    ? 'border-blue-500 bg-blue-500/20' 
                    : 'border-slate-600 hover:border-slate-500'
                  }
                `}
              >
                <div className="w-10 h-10 mx-auto rounded-full bg-gradient-to-br from-blue-400 to-blue-600 mb-2" />
                <div className="text-sm">Blue (First)</div>
              </button>
              <button
                onClick={() => setPlayerNumber(2)}
                className={`
                  flex-1 p-4 rounded-xl border-2 transition-all
                  ${playerNumber === 2 
                    ? 'border-amber-500 bg-amber-500/20' 
                    : 'border-slate-600 hover:border-slate-500'
                  }
                `}
              >
                <div className="w-10 h-10 mx-auto rounded-full bg-gradient-to-br from-amber-400 to-amber-600 mb-2" />
                <div className="text-sm">Orange</div>
              </button>
            </div>
          </div>
          
          {/* Difficulty */}
          <div>
            <label className="text-sm text-gray-400 mb-2 block">AI Difficulty</label>
            <div className="grid grid-cols-4 gap-2">
              {['easy', 'medium', 'hard', 'expert'].map((d) => (
                <Button
                  key={d}
                  variant={difficulty === d ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setDifficulty(d)}
                  className={difficulty === d ? 'bg-cyan-600' : 'border-slate-600'}
                >
                  {d.charAt(0).toUpperCase() + d.slice(1)}
                </Button>
              ))}
            </div>
          </div>
        </div>
        
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1 border-slate-600">
            Cancel
          </Button>
          <Button 
            onClick={() => onStart({ playerNumber, difficulty })}
            className="flex-1 bg-cyan-600 hover:bg-cyan-700"
          >
            Start Game
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Game Over Dialog
const GameOverDialog = ({ isOpen, winner, playerNumber, onNewGame, onClose }) => {
  const playerWon = winner === playerNumber;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-cyan-500/30 text-white max-w-sm text-center">
        <div className="py-6">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className={`
              w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4
              ${playerWon 
                ? 'bg-gradient-to-br from-yellow-400 to-amber-500' 
                : 'bg-gradient-to-br from-gray-600 to-gray-800'
              }
            `}
          >
            <Trophy className={`w-10 h-10 ${playerWon ? 'text-white' : 'text-gray-400'}`} />
          </motion.div>
          
          <h2 className={`text-2xl font-bold mb-2 ${playerWon ? 'text-yellow-400' : 'text-gray-400'}`}>
            {playerWon ? 'Victory!' : 'Defeat'}
          </h2>
          <p className="text-gray-400">
            {playerWon 
              ? 'You moved all pieces to the goal!' 
              : 'The AI reached the goal first!'
            }
          </p>
        </div>
        
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1 border-slate-600">
            Review
          </Button>
          <Button onClick={onNewGame} className="flex-1 bg-cyan-600 hover:bg-cyan-700">
            New Game
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Triangle Checkers Game Component
const TriangleCheckersGameComponent = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [game, setGame] = useState(null);
  const [playerNumber, setPlayerNumber] = useState(1);
  const [difficulty, setDifficulty] = useState('medium');
  const [selectedCell, setSelectedCell] = useState(null);
  const [validMoves, setValidMoves] = useState([]);
  const [isAIThinking, setIsAIThinking] = useState(false);
  const [gameStatus, setGameStatus] = useState('idle');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [gameStartTime, setGameStartTime] = useState(null);
  
  const [showNewGame, setShowNewGame] = useState(true);
  const [showGameOver, setShowGameOver] = useState(false);
  
  // Helper to save game result
  const saveGameResult = useCallback(async (result, moveCount) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats/record-game`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          result,
          opponent_type: 'ai',
          difficulty,
          player_color: playerNumber === 1 ? 'blue' : 'orange',
          total_moves: moveCount,
          game_duration_seconds: gameStartTime ? Math.floor((Date.now() - gameStartTime) / 1000) : 0,
          game_type: 'triangle_checkers',
          variant: 'hexagonal'
        })
      });
      if (response.ok) {
        console.log('Triangle Checkers game result saved');
      }
    } catch (error) {
      console.log('Failed to save game result (user may not be logged in)');
    }
  }, [difficulty, playerNumber, gameStartTime]);
  
  // Hex size based on screen
  const hexSize = useMemo(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 640 ? 16 : 22;
    }
    return 22;
  }, []);
  
  // Board layout from game
  const boardLayout = useMemo(() => {
    if (!game) return [];
    return game.getBoardLayout();
  }, [game, game?.pieces]);
  
  // Calculate SVG viewBox
  const viewBox = useMemo(() => {
    if (boardLayout.length === 0) return "-200 -200 400 400";
    
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    
    for (const cell of boardLayout) {
      minX = Math.min(minX, cell.x);
      minY = Math.min(minY, cell.y);
      maxX = Math.max(maxX, cell.x);
      maxY = Math.max(maxY, cell.y);
    }
    
    const padding = hexSize * 2;
    return `${minX - padding} ${minY - padding} ${maxX - minX + padding * 2} ${maxY - minY + padding * 2}`;
  }, [boardLayout, hexSize]);
  
  // Start new game
  const startNewGame = useCallback((options) => {
    const newGame = new TriangleCheckersGame();
    setGame(newGame);
    setPlayerNumber(options.playerNumber);
    setDifficulty(options.difficulty);
    setSelectedCell(null);
    setValidMoves([]);
    setGameStatus('playing');
    setShowNewGame(false);
    setShowGameOver(false);
    setIsAIThinking(false);
    setGameStartTime(Date.now());
    
    // If AI goes first
    if (options.playerNumber === 2) {
      setTimeout(() => makeAIMove(newGame, options.difficulty), 500);
    }
  }, []);
  
  // Make AI move
  const makeAIMove = useCallback((currentGame, diff) => {
    if (!currentGame || currentGame.gameOver) return;
    
    setIsAIThinking(true);
    
    setTimeout(() => {
      const result = executeAIMove(currentGame, diff);
      
      if (result) {
        // Force re-render by creating new game reference
        setGame(currentGame.clone());
        
        if (soundEnabled) {
          const audio = new Audio('/move.mp3');
          audio.volume = 0.3;
          audio.play().catch(() => {});
        }
        
        if (currentGame.gameOver) {
          setGameStatus('gameOver');
          setShowGameOver(true);
          // Save result - AI won means player lost
          const playerWon = currentGame.winner === playerNumber;
          saveGameResult(playerWon ? 'win' : 'loss', currentGame.moveHistory.length);
        }
      }
      
      setIsAIThinking(false);
    }, 400);
  }, [soundEnabled, playerNumber, saveGameResult]);
  
  // Handle cell click
  const handleCellClick = useCallback((cell) => {
    if (!game || isAIThinking || gameStatus !== 'playing') return;
    if (game.currentPlayer !== playerNumber) return;
    
    const piece = game.getPiece(cell.q, cell.r);
    
    if (selectedCell) {
      // Check if this is a valid move target
      const isValidTarget = validMoves.some(m => m.q === cell.q && m.r === cell.r);
      
      if (isValidTarget) {
        // Make the move
        const success = game.makeMove(selectedCell.q, selectedCell.r, cell.q, cell.r);
        
        if (success) {
          if (soundEnabled) {
            const audio = new Audio('/move.mp3');
            audio.volume = 0.3;
            audio.play().catch(() => {});
          }
          
          // Force re-render
          setGame(game.clone());
          setSelectedCell(null);
          setValidMoves([]);
          
          if (game.gameOver) {
            setGameStatus('gameOver');
            setShowGameOver(true);
            // Player won
            const playerWon = game.winner === playerNumber;
            saveGameResult(playerWon ? 'win' : 'loss', game.moveHistory.length);
          } else {
            // AI's turn
            setTimeout(() => makeAIMove(game, difficulty), 500);
          }
        }
      } else if (piece && piece.player === playerNumber) {
        // Select different piece
        setSelectedCell({ q: cell.q, r: cell.r });
        setValidMoves(game.getValidMoves(cell.q, cell.r));
      } else {
        // Deselect
        setSelectedCell(null);
        setValidMoves([]);
      }
    } else if (piece && piece.player === playerNumber) {
      // Select this piece
      setSelectedCell({ q: cell.q, r: cell.r });
      setValidMoves(game.getValidMoves(cell.q, cell.r));
    }
  }, [game, selectedCell, validMoves, playerNumber, isAIThinking, gameStatus, difficulty, soundEnabled, makeAIMove]);
  
  // Piece counts
  const pieceCounts = useMemo(() => {
    if (!game) return { player1: 0, player2: 0, player1InGoal: 0, player2InGoal: 0 };
    return game.getPieceCounts();
  }, [game, game?.pieces]);
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-cyan-950/20 to-slate-950">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-md border-b border-cyan-500/20 py-3 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/games/checkers')}
              className="text-gray-400 hover:text-white"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            
            <div className="flex items-center gap-2">
              <Target className="w-6 h-6 text-cyan-400" />
              <h1 className="text-xl font-bold text-cyan-400">Triangle Checkers</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="text-gray-400 hover:text-white"
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </Button>
            
            <Button
              size="sm"
              onClick={() => setShowNewGame(true)}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">New Game</span>
            </Button>
          </div>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Game Info */}
        {game && (
          <div className="flex items-center justify-between mb-4 px-2">
            <div className={`
              flex items-center gap-2 px-3 py-2 rounded-lg
              ${game.currentPlayer === 1 
                ? 'bg-blue-500/20 border border-blue-500/30' 
                : 'bg-amber-500/20 border border-amber-500/30'
              }
            `}>
              <div className={`w-4 h-4 rounded-full ${game.currentPlayer === 1 ? 'bg-blue-500' : 'bg-amber-500'}`} />
              <span className="text-white text-sm font-medium">
                {game.currentPlayer === playerNumber ? 'Your Turn' : 'AI Thinking...'}
              </span>
              {isAIThinking && <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />}
            </div>
            
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="text-gray-400">{pieceCounts.player1InGoal}/{pieceCounts.player1}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                <span className="text-gray-400">{pieceCounts.player2InGoal}/{pieceCounts.player2}</span>
              </div>
            </div>
          </div>
        )}
        
        {/* Board */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative bg-slate-900/60 rounded-2xl border border-cyan-500/30 p-4 overflow-hidden"
        >
          {/* Legend */}
          <div className="absolute top-2 left-2 flex flex-col gap-1 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-blue-600/60" />
              <span className="text-blue-400">Blue Start</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-amber-600/60" />
              <span className="text-amber-400">Blue Goal</span>
            </div>
          </div>
          
          {game ? (
            <svg 
              viewBox={viewBox}
              className="w-full h-auto max-h-[70vh]"
              style={{ minHeight: '400px' }}
            >
              {boardLayout.map((cell) => (
                <HexCell
                  key={cell.key}
                  cell={cell}
                  piece={cell.piece}
                  isSelected={selectedCell?.q === cell.q && selectedCell?.r === cell.r}
                  isValidMove={validMoves.some(m => m.q === cell.q && m.r === cell.r)}
                  onClick={() => handleCellClick(cell)}
                  hexSize={hexSize}
                />
              ))}
            </svg>
          ) : (
            <div className="h-[400px] flex items-center justify-center text-gray-500">
              Start a new game to begin
            </div>
          )}
        </motion.div>
        
        {/* Instructions */}
        <div className="mt-4 bg-slate-800/50 rounded-xl p-4 border border-slate-700">
          <h3 className="text-cyan-400 font-semibold mb-2 flex items-center gap-2">
            <Target className="w-4 h-4" />
            How to Play
          </h3>
          <ul className="text-gray-400 text-sm space-y-1">
            <li className="flex items-start gap-2">
              <ArrowRight className="w-4 h-4 mt-0.5 text-gray-500" />
              Move your pieces from your starting corner to the opposite corner
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="w-4 h-4 mt-0.5 text-gray-500" />
              Click a piece to select, then click a valid destination
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="w-4 h-4 mt-0.5 text-gray-500" />
              Hop over any piece (yours or opponent's) to move faster - chain jumps allowed!
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="w-4 h-4 mt-0.5 text-gray-500" />
              First player to get all pieces to the goal wins
            </li>
          </ul>
        </div>
      </main>
      
      {/* Dialogs */}
      <NewGameDialog
        isOpen={showNewGame}
        onClose={() => game && setShowNewGame(false)}
        onStart={startNewGame}
      />
      
      <GameOverDialog
        isOpen={showGameOver}
        winner={game?.winner}
        playerNumber={playerNumber}
        onNewGame={() => {
          setShowGameOver(false);
          setShowNewGame(true);
        }}
        onClose={() => setShowGameOver(false)}
      />
    </div>
  );
};

export default TriangleCheckersGameComponent;
